import React, { useState } from 'react';
import { Calendar, User, ArrowRight } from 'lucide-react';
import BlobButton from '../components/BlobButton';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Writing Tips', 'Publishing', 'Marketing', 'Author Success'];

  const blogPosts = [
    {
      title: '10 Tips for Writing Your First Novel',
      excerpt: 'Starting your first novel can be daunting. Here are proven strategies to help you get started and stay motivated through the writing process.',
      image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&h=500&fit=crop',
      category: 'Writing Tips',
      author: 'Sarah Johnson',
      date: 'October 15, 2024',
      readTime: '8 min read'
    },
    {
      title: 'Self-Publishing vs Traditional Publishing: A 2024 Guide',
      excerpt: 'Exploring the pros and cons of each publishing route to help you make the best decision for your book and career goals.',
      image: 'https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=800&h=500&fit=crop',
      category: 'Publishing',
      author: 'Michael Chen',
      date: 'October 12, 2024',
      readTime: '10 min read'
    },
    {
      title: 'How to Market Your Book on Social Media',
      excerpt: 'Learn effective social media strategies that actually work for book promotion, including platform-specific tips and content ideas.',
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=500&fit=crop',
      category: 'Marketing',
      author: 'Emily Rodriguez',
      date: 'October 8, 2024',
      readTime: '12 min read'
    },
    {
      title: 'From Manuscript to Bestseller: Author Success Story',
      excerpt: 'Meet Jane Smith, who turned her self-published debut into a bestseller. Learn the strategies and mindset that made it possible.',
      image: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800&h=500&fit=crop',
      category: 'Author Success',
      author: 'David Thompson',
      date: 'October 5, 2024',
      readTime: '15 min read'
    },
    {
      title: 'The Ultimate Guide to Book Cover Design',
      excerpt: 'Your book cover is your first impression. Discover what makes a great cover and how to work with designers to create one.',
      image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&h=500&fit=crop',
      category: 'Publishing',
      author: 'Sarah Johnson',
      date: 'October 1, 2024',
      readTime: '9 min read'
    },
    {
      title: 'Building Your Author Platform: A Step-by-Step Guide',
      excerpt: 'Learn how to build an author platform that attracts readers and creates lasting connections with your audience.',
      image: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=500&fit=crop',
      category: 'Marketing',
      author: 'Emily Rodriguez',
      date: 'September 28, 2024',
      readTime: '11 min read'
    }
  ];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Publishing <span className="text-amber-700">Insights</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Expert advice, tips, and inspiration for authors on their publishing journey.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white sticky top-20 z-40 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-amber-700 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {filteredPosts.map((post, index) => (
              <article key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
                <div className="relative overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-amber-700 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{post.readTime}</span>
                    <button className="flex items-center gap-2 text-amber-700 font-semibold hover:gap-3 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-br from-amber-700 to-amber-600">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-4xl font-bold mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-xl mb-8 opacity-90">
              Get the latest publishing tips, author success stories, and industry insights delivered to your inbox.
            </p>
            <div className="flex gap-4 max-w-md mx-auto items-center">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full text-gray-900 outline-none text-base"
              />
              <BlobButton className="whitespace-nowrap">
                Subscribe
              </BlobButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
